import { findResolveMapEntry } from '@ms-cloudpack/package-utilities';
import type { BundleRequest } from '../types/BundleRequest.js';
import { createBundleTask } from '../utilities/createBundleTask.js';
import type { Context } from '../types/Context.js';
import type { WatchOptions } from '@ms-cloudpack/file-watcher';
import { getConsumedDependencies } from '../utilities/getConsumedDependencies.js';
import type { TargetEnvironment, WithOptional } from '@ms-cloudpack/common-types';
import { notifyHost } from '../utilities/notifyHost.js';

/**
 * Input for `ensurePackageBundled`.
 */
export interface EnsurePackageBundledInput extends Pick<
  BundleRequest,
  'outputPath' | 'version' | 'disableSourceMaps' | 'shouldGetBundleInfo'
> {
  /** Package name */
  name: string;
  /**
   * Target environment for the bundle.
   * @default 'browser'
   */
  targetEnvironment?: TargetEnvironment;
  /** Whether to also ensure dependencies are bundled */
  enqueueDependencies?: boolean;
  /** Whether to re-run the bundle task */
  shouldRerun?: boolean;
  /**
   * Whether to watch for changes (internal packages only).
   * NOTE: requires `ctx.bus` and `ctx.watcher` to be set.
   */
  shouldWatch?: boolean;
  /** Whether to force re-bundling */
  shouldForce?: boolean;
  /** Whether to disable caching. */
  disableCache?: boolean;
  /** Whether to support incremental rebuilds. Defaults to true for internal libraries. */
  isIncremental?: boolean;
  /**
   * Whether to use a hash as the bundle id instead of the path.
   * Reduces performance when used with shouldForce as it hashes twice,
   * but eliminates task cache artifacts when handling many apps.
   */
  useHashAsId?: boolean;
  /** Priority for bundling. Greater priority will be scheduled first (defaults to 0). */
  priority?: number;
}

/**
 * Result of ensuring a single package is bundled.
 */
export interface EnsurePackageBundledResult extends Omit<
  NonNullable<BundleRequest['result']>,
  'rebuild' | 'dispose' | 'bundlerName' | 'inputPath'
> {
  /** Package name */
  name: string;
  /** Package version */
  version: string;
  /** Package dependency names (prod and peer) */
  dependencies?: string[];
  /** Whether the package is external (not defined in this repo). Unset if it's not found. */
  isExternal?: boolean;
  /** Absolute path of the package. Will be unset if the package couldn't be found. */
  inputPath?: string;
}

/**
 * Complete output for `ensurePackageBundled`.
 */
export interface EnsurePackageBundledOutput {
  /** Result of bundling this package */
  result: EnsurePackageBundledResult;
  /** Results of bundling dependencies */
  dependencies: Promise<EnsurePackageBundledResult[]>;
}

/**
 * Context for `ensurePackageBundled`. `bus` and `watcher` are only needed if `shouldWatch` is true.
 */
export type EnsurePackageBundledContext = WithOptional<Context, 'bus' | 'watcher'>;

/**
 * Checks if the package is bundled or needs bundling, and returns the result.
 * For internal packages, we also notify the watcher to monitor for code changes.
 */
export async function ensurePackageBundled(
  input: EnsurePackageBundledInput,
  ctx: EnsurePackageBundledContext,
): Promise<EnsurePackageBundledOutput> {
  const results: EnsurePackageBundledResult[] = [];
  const visited = new Set<`${string}@${string}`>();

  // Bundle the package itself
  let result: EnsurePackageBundledResult;
  let mainDependencies: EnsurePackageBundledInput[];
  try {
    ({ result, dependencyInputs: mainDependencies } = await ensurePackageBundledInternal({ ...input, visited }, ctx));
  } catch (err) {
    return {
      result: getErrorResult(input, err),
      dependencies: Promise.resolve([]),
    };
  }

  // Enqueue main dependencies
  // TODO: should processing this queue have a rate limit? right now if enqueueDependencies is enabled,
  // `await dependencies` will fire off all deps at once.
  let pending = 0;
  const dependencies = new Promise<EnsurePackageBundledResult[]>((resolve) => {
    const enqueue = (dependencyInputs: EnsurePackageBundledInput[]) => {
      for (const dependencyInput of dependencyInputs) {
        pending++;

        void ensurePackageBundledInternal({ ...dependencyInput, visited }, ctx)
          .then((dependencyBundleResult) => {
            // Save result and enqueue this dep's deps
            results.push(dependencyBundleResult.result);
            enqueue(dependencyBundleResult.dependencyInputs);
          })
          .catch((err) => {
            // If there's an error (most likely that the dependency wasn't found in the resolve map),
            // save it in a result instead of throwing. This generally gives better logging.
            results.push(getErrorResult(dependencyInput, err));
          })
          .then(() => {
            pending--;
            if (!pending) {
              resolve(results);
            }
          });
      }
    };

    enqueue(mainDependencies);
  });

  return {
    result,
    dependencies,
  };
}

async function ensurePackageBundledInternal(
  params: EnsurePackageBundledInput & {
    visited: Set<`${string}@${string}`>;
  },
  ctx: EnsurePackageBundledContext,
): Promise<{ result: EnsurePackageBundledResult; dependencyInputs: EnsurePackageBundledInput[] }> {
  const { taskRunner, session, watcher, packageHashes, bus, telemetryClient } = ctx;
  const { resolveMap, config } = session;
  const {
    name,
    version,
    shouldWatch,
    disableSourceMaps = config.features?.disableSourceMaps,
    enqueueDependencies = config.features?.enqueueDependencies,
    outputPath,
    shouldGetBundleInfo,
    shouldRerun,
    shouldForce,
    disableCache,
    isIncremental,
    useHashAsId,
    priority,
    targetEnvironment = 'browser',
    visited,
  } = params;

  const dependencyInputs: EnsurePackageBundledInput[] = [];

  visited.add(`${name}@${version}`);

  // First, we find the resolve map entry. This helps us resolve the physical location of the package
  // according to the resolveMap used in the session. (This can change based on linking.)
  const packageEntry = findResolveMapEntry({ packageName: name, version, resolveMap });

  if (!packageEntry) {
    throw new Error(`Could not find package ${version ? `${name}@${version}` : name} in the resolve map.`);
  }

  const isExternal = !!packageEntry.isExternal;

  let hash: string | undefined;
  let sourceHash: string | undefined;

  if (useHashAsId) {
    // If we are using the hash as the task id, we need to calculate it once and pass it to the task
    // to prevent a race condition in which we get a different hash than the one used in the task id.
    hash = await packageHashes.get({
      packagePath: packageEntry.path,
      isSourceHashingEnabled: false,
      shouldRecalculate: true,
      targetEnvironment,
    });

    if (!packageEntry.isExternal) {
      // We only need the source hash for internal packages for cache validation.
      sourceHash = await packageHashes.get({
        packagePath: packageEntry.path,
        isSourceHashingEnabled: true,
        shouldRecalculate: true,
        targetEnvironment,
      });
    }
  }

  const bundleRequest: BundleRequest = {
    id: hash ?? `${packageEntry.path}--env:${targetEnvironment}`,
    packagePath: packageEntry.path,
    packageName: name,
    targetEnvironment,
    isExternal,
    isIncremental,
    outputPath,
    version,
    disableSourceMaps,
    shouldGetBundleInfo: shouldGetBundleInfo || enqueueDependencies,
    disableCache,
    mode: config.mode,
  };

  // Make priority negative to have retry count set to positive values.
  const retryCount = priority ? -1 * priority : undefined;

  const task = createBundleTask({ bundleRequest, force: shouldForce, hash, sourceHash, retryCount }, ctx);

  // TODO: Add lage scheduling back.
  const { result } = await taskRunner.add(task, { rerun: shouldRerun, priority });

  if (!result) {
    throw new Error('Bundle failed, no result');
  }

  // For internal packages, make sure we're watching the source to re-trigger bundling.
  if (shouldWatch && !isExternal) {
    if (!bus || !watcher) {
      throw new Error('bus and watcher must be provided in context if shouldWatch is true (this is a bug).');
    }

    const definition = await ctx.packages.get(packageEntry.path);
    const watchOptions: WatchOptions = {
      id: bundleRequest.id,
      path: packageEntry.path,
      watchPaths: definition.cloudpack?.inputPaths,
    };

    const onPackageChange = async () => {
      const watchedResult = await taskRunner.add(task, { rerun: true });

      // If the result was successful, notify clients about the package update (HMR or full reload)
      if (watchedResult.result?.errors?.length === 0) {
        await notifyHost(
          { reason: 'on-package-change', packageEntry, hash: watchedResult.result.hash },
          { bus, session, telemetryClient },
        );
      }
    };
    await watcher.watch(watchOptions, (changedPaths) => {
      // Filter out the configPaths, we don't want to re-bundle on config changes. CLI will restart it instead.
      const filteredPaths = changedPaths.filter((p) => !config.configPaths?.appConfigPaths.includes(p));
      if (filteredPaths.length > 0) {
        void onPackageChange();
      }
    });
  }

  // If enabled, enqueue dependencies.
  if (enqueueDependencies && result.info) {
    for (const dependencyName of getConsumedDependencies(result.info)) {
      const dependencyVersion = packageEntry.dependencies[dependencyName];

      // If we've already visited this package from another package, skip it.
      if (visited.has(`${dependencyName}@${dependencyVersion}`)) {
        continue;
      }

      dependencyInputs.push({
        name: dependencyName,
        version: dependencyVersion,
        shouldWatch,
        enqueueDependencies,
        shouldGetBundleInfo,
      });
    }
  }

  return {
    result: {
      version,
      name,
      isExternal,
      ...result,
    },
    dependencyInputs,
  };
}

function getErrorResult(input: EnsurePackageBundledInput, error: unknown): EnsurePackageBundledResult {
  const text = `Error thrown while bundling: ${error instanceof Error ? error.stack || error.message : error}`;
  return {
    name: input.name,
    version: input.version,
    errors: [{ text, source: 'ensurePackageBundled' }],
  };
}

// This function doesn't have an attached procedure because it's never called via the TRPC server.
